import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { TenantSummary } from '@cvsi/shared';
import { useClient } from './staffContext';

const STORAGE_KEY = 'cvsi_admin_selected_company';

interface CompanyContextValue {
  companies: TenantSummary[];
  loading: boolean;
  error: Error | null;
  selectedCompanyId: string;
  selectedCompany: TenantSummary | null;
  selectCompany: (tenantId: string) => void;
  reload: () => void;
}

const Ctx = createContext<CompanyContextValue | null>(null);

function readStoredSelection(): string {
  try { return localStorage.getItem(STORAGE_KEY) ?? ''; } catch { return ''; }
}

/**
 * Global "which company am I looking at" selector for the admin portal — every tenant-scoped page
 * (Review Queue, Audit Chain, Live Access) reads from this instead of keeping its own picker, so a
 * company chosen once stays selected while navigating between them.
 */
export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const client = useClient();
  const [companies, setCompanies] = useState<TenantSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(readStoredSelection);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    client.listTenants()
      .then((res) => { if (alive) setCompanies(res.items); })
      .catch((e) => { if (alive) setError(e as Error); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [client, nonce]);

  // Drop a stale selection (deleted/renamed tenant, or localStorage left over from another environment).
  useEffect(() => {
    if (loading || !selectedCompanyId) return;
    if (!companies.some((c) => c.tenantId === selectedCompanyId)) setSelectedCompanyId('');
  }, [companies, loading, selectedCompanyId]);

  const selectCompany = useCallback((tenantId: string) => {
    setSelectedCompanyId(tenantId);
    try {
      if (tenantId) localStorage.setItem(STORAGE_KEY, tenantId);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* localStorage unavailable (private mode) — selection still works in-memory */ }
  }, []);

  const selectedCompany = useMemo(
    () => companies.find((c) => c.tenantId === selectedCompanyId) ?? null,
    [companies, selectedCompanyId],
  );

  const value = useMemo<CompanyContextValue>(() => ({
    companies, loading, error, selectedCompanyId, selectedCompany, selectCompany,
    reload: () => setNonce((n) => n + 1),
  }), [companies, loading, error, selectedCompanyId, selectedCompany, selectCompany]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCompany(): CompanyContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useCompany must be used within <CompanyProvider>');
  return v;
}
